import classnames from 'classnames';
import { Utensils } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useTableOrder from '../../hooks/order/useTableOrder';
import useTableOrderStatus from '../../hooks/order/useTableOrderStatus';
import AvailabilityNote from './AvailabilityNote';
import TableName from './TableName';

interface Props {
  className?: string;
}

/**
 * On top of the cart at a table: "Table ordering · Table 7", as soon as possible. No Change: the table
 * comes from its QR code. While the restaurant is closed or paused it says why checkout is off.
 */
export default function TableOrderLine({ className }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });
  const { isAtTable, table } = useTableOrder();
  const { state, opensAt, servedIn } = useTableOrderStatus();

  if (!isAtTable) {
    return null;
  }

  return (
    <div className={classnames('space-y-[12px]', className)} data-test="table-order-line">
      <div className={box}>
        <Utensils aria-hidden="true" className="w-[18px] h-[18px] shrink-0" />
        <p className="flex-1 min-w-0 m-0 text-[14px] text-brand-text-primary">
          <b className="font-bold">{t('table_ordering')}</b> · <TableName name={table.name} />
          <br />
          <small className="text-[13px] text-muted-foreground">
            {t('asap')} · {t('served_in', { time: servedIn })}
          </small>
        </p>
      </div>
      {state !== 'open' && (
        <AvailabilityNote
          variant="warning"
          title={state === 'closed' && !opensAt ? t('cart.closed_later') : t(`cart.${state}`, { time: opensAt })}
          testId={`table-cart-${state}-note`}
        />
      )}
    </div>
  );
}

// Values from the design (Cart · order type).
const box = 'flex items-center gap-[12px] p-[12px] rounded-[12px] bg-muted text-brand-text-primary';
